import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "@/common/components/ui/button";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/common/components/ui/card";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import { Switch, message } from "antd";
import finEaseHomeNavigationRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";
import ScreenerService from "@/services/FinEaseHomeAndAdmin/APIs/ScrennerService";

interface CategoryPayload {
  title: string;
  sub_title: string;
  enable: boolean;
}

// Removed staticCategories

const AddEditCategoryMaster = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEdit = !!id;
  const isViewMode = location.pathname.includes("/view/");

  const [formData, setFormData] = useState<CategoryPayload>({
    title: "",
    sub_title: "",
    enable: true,
  });

  const [isLoading, setIsLoading] = useState(false);

  const fetchCategoryDetails = async (categoryId: string) => {
    setIsLoading(true);
    try {
      const response = await ScreenerService.getCategoryById(categoryId);
      if (response && response.success !== false && response.data) {
        setFormData({
          title: response.data.title ?? "",
          sub_title: response.data.sub_title ?? "",
          enable: response.data.is_enabled ?? true,
        });
      } else {
        message.warning("Failed to fetch category details");
      }
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message ??
          "Something went wrong while fetching category details."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if ((isEdit || isViewMode) && id) {
      fetchCategoryDetails(id);
    }
  }, [id, isEdit, isViewMode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isViewMode) {
      navigate(finEaseHomeNavigationRoutes.CATEGORY_MASTER);
      return;
    }

    setIsLoading(true);
    try {
      if (isEdit && id) {
        const response: any = await ScreenerService.updateCategoryById(id, {
          title: formData.title,
          sub_title: formData.sub_title,
          // If you need to send enable status, you can add it to CreateCategoryPayload or a new interface
        });

        if (response && response.success !== false) {
          message.success(response.message ?? "Category updated successfully!");
          navigate(finEaseHomeNavigationRoutes.CATEGORY_MASTER);
        } else {
          message.warning(response.message ?? "Failed to update category");
        }
      } else {
        const response: any = await ScreenerService.createQueryCategory({
          title: formData.title,
          sub_title: formData.sub_title,
        });

        if (response && response.success !== false) {
          message.success(response.message ?? "Category created successfully!");
          navigate(finEaseHomeNavigationRoutes.CATEGORY_MASTER);
        } else {
          message.warning(response.message ?? "Failed to create category");
        }
      }
    } catch (error: any) {
      console.error(error);
      const errorData = error?.response?.data?.error ?? error?.response?.data;

      if (errorData?.fields && typeof errorData.fields === "object") {
        Object.entries(errorData.fields).forEach(([field, messages]) => {
          const msgs = Array.isArray(messages)
            ? messages.join(", ")
            : String(messages);
          const formattedField = field
            .replace(/_/g, " ")
            .replace(/\b\w/g, c => c.toUpperCase());
          message.error(`${formattedField}: ${msgs}`);
        });
      } else {
        message.error(
          errorData?.message ?? "An error occurred while saving the category."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50/50 font-sans text-sm">
      <form onSubmit={handleSubmit} className="flex h-full flex-col">
        <div className="sticky top-0 z-30 border-b border-slate-200 bg-white">
          <div className="mx-auto w-full max-w-5xl">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-5">
                <Button
                  variant="outline"
                  size="icon"
                  type="button"
                  onClick={() =>
                    navigate(finEaseHomeNavigationRoutes.CATEGORY_MASTER)
                  }
                  className="h-10 w-10 border-slate-200 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-5 w-5 text-slate-600" />
                </Button>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                    {isViewMode
                      ? `View Category`
                      : isEdit
                        ? `Edit Category`
                        : "Create New Category"}
                  </h1>
                  <p className="mt-0.5 text-sm font-medium text-slate-500">
                    Configure Screener Category details
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {isViewMode ? (
                  <Button
                    type="button"
                    variant="default"
                    onClick={() => navigate("/admin/screener/category-master")}
                    className="h-10 bg-primary px-5 text-sm font-semibold text-white"
                  >
                    Back to List
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="h-11 gap-2.5 bg-primary px-8 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 hover:bg-primary/95"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {isEdit ? "Save Changes" : "Create Category"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-1 overflow-auto bg-slate-50/50">
          <div className="mx-auto w-full max-w-5xl flex-1 p-6">
            <Card className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
              <CardContent className="space-y-8 p-8">
                <div>
                  <h3 className="mb-6 border-b pb-3 text-lg font-bold text-primary">
                    Category Information
                  </h3>
                  <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                    <div className="space-y-3">
                      <Label
                        htmlFor="title"
                        className="text-sm font-semibold text-slate-700"
                      >
                        Category Title
                      </Label>
                      <Input
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="e.g. Large Cap"
                        disabled={isViewMode}
                        className="h-11 border-slate-200 bg-slate-50/50 transition-colors placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
                        required
                      />
                    </div>

                    <div className="space-y-3">
                      <Label
                        htmlFor="sub_title"
                        className="text-sm font-semibold text-slate-700"
                      >
                        Sub Title
                      </Label>
                      <Input
                        id="sub_title"
                        name="sub_title"
                        value={formData.sub_title}
                        onChange={handleInputChange}
                        placeholder="e.g. For blue chip companies"
                        disabled={isViewMode}
                        className="h-11 border-slate-200 bg-slate-50/50 transition-colors placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
                        required
                      />
                    </div>

                    <div className="space-y-3 md:col-start-1">
                      <Label className="block text-sm font-semibold text-slate-700">
                        Status
                      </Label>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={formData.enable}
                          onChange={checked =>
                            setFormData({ ...formData, enable: checked })
                          }
                          disabled={isViewMode}
                          style={{
                            backgroundColor: formData.enable
                              ? "#66A0CD"
                              : "#9ca3af",
                          }}
                        />
                        <span
                          className={`text-sm font-medium ${formData.enable ? "text-brandPrimary" : "text-slate-500"}`}
                        >
                          {formData.enable ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddEditCategoryMaster;
